import type { Graph3DData, Graph3DPositionedNode } from '../core/graph.types';
import type { Graph3DLayoutConfig } from '../core/layout.types';
import { normalizeDepth } from './coord';

export function applyPresetLayout<TNodeData = unknown>(
  data: Graph3DData<TNodeData>,
  config: Graph3DLayoutConfig = {}
): Graph3DPositionedNode<TNodeData>[] {
  const zGap = config.zGap ?? 24;
  const nodes = data.nodes.map((node, index) => ({
    ...node,
    resolvedPosition: {
      x: node.position?.x ?? index * 180,
      y: node.position?.y ?? 0,
      z: node.position?.z ?? normalizeDepth(node.depth, zGap),
    },
    resolvedSize: {
      width: node.size?.width ?? 140,
      height: node.size?.height ?? 64,
      depth: node.size?.depth ?? 10,
    },
  }));
  if (!config.center || nodes.length === 0) return nodes;
  const minX = Math.min(
    ...nodes.map(node => node.resolvedPosition.x - node.resolvedSize.width / 2)
  );
  const maxX = Math.max(
    ...nodes.map(node => node.resolvedPosition.x + node.resolvedSize.width / 2)
  );
  const minY = Math.min(
    ...nodes.map(node => node.resolvedPosition.y - node.resolvedSize.height / 2)
  );
  const maxY = Math.max(
    ...nodes.map(node => node.resolvedPosition.y + node.resolvedSize.height / 2)
  );
  const minZ = Math.min(
    ...nodes.map(node => node.resolvedPosition.z - node.resolvedSize.depth / 2)
  );
  const maxZ = Math.max(
    ...nodes.map(node => node.resolvedPosition.z + node.resolvedSize.depth / 2)
  );
  const center = {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    z: (minZ + maxZ) / 2,
  };
  return nodes.map(node => ({
    ...node,
    resolvedPosition: {
      x: node.resolvedPosition.x - center.x,
      y: node.resolvedPosition.y - center.y,
      z: node.resolvedPosition.z - center.z,
    },
  }));
}
